import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useT } from "@/app/localization";

export type OrgRole = "manager" | "driver" | null | undefined;

/**
 * Renders an organization-membership role as a consistent, self-explaining badge
 * so the two org roles read the same everywhere they appear (org detail, global
 * users list, user access panel).
 *
 * These are the TENANT-plane roles (`organization_users.role`), distinct from the
 * system role (`admin`/`user`). See `organizations/authz.py` and
 * `docs/architecture/adr-rbac-permission-layer.md`.
 *
 *   manager → full org management  (branded/primary)
 *   driver  → restricted charging-only identity  (info)
 *   null    → not a member (or unknown) → nothing, unless `showNone`
 */
export function OrgRoleBadge({
  role,
  showNone = false,
  className,
  title,
}: {
  role: OrgRole;
  /** When the user is not a member, render a muted "—" instead of nothing. */
  showNone?: boolean;
  className?: string;
  /** Override the default explanatory tooltip (e.g. to list the org names). */
  title?: string;
}) {
  const t = useT();
  if (role === "manager") {
    return (
      <Badge
        variant="violet"
        className={cn("badge-sm", className)}
        title={title ?? t("operator.org_role.manager_hint")}
      >
        {t("operator.org_role.manager")}
      </Badge>
    );
  }
  if (role === "driver") {
    return (
      <Badge
        variant="info"
        className={cn("badge-sm", className)}
        title={title ?? t("operator.org_role.driver_hint")}
      >
        {t("operator.org_role.driver")}
      </Badge>
    );
  }
  if (showNone) {
    return (
      <Badge variant="neutral" className={cn("badge-sm", className)} title={title ?? t("operator.org_role.none_hint")}>
        —
      </Badge>
    );
  }
  return null;
}
